import ListTypeDetailActions from '@/components/feature/list-type/list-type-detail-actions';
import BackIcon from '@/components/icons/BackIcon';
import type { ListTypeListItem } from '@/types/list-type';
import { formatKoreanDateTTS, formatListTypeDateRange } from '@/utils/date-time';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useConfigStore } from '@/stores';
import clsx from 'clsx';
import { CalendarIcon } from '@/components/icons/calendarIcon';
import { getS3UploadUrl, isS3UploadUrl } from '@/utils/s3Config';
import { ChevronUpIcon } from '@/components/icons';
import { useCallback, useRef, useState } from 'react';
import { useAccessibilityTTS } from '@/hooks';

interface ListTypeDetailVerticalProps {
    detail: ListTypeListItem;
    onBack: () => void;
    onQrClick: () => void;
    onDirectionClick: (detail: ListTypeListItem) => void;
}

const ListTypeDetailVertical = ({ detail, onBack, onQrClick, onDirectionClick }: ListTypeDetailVerticalProps) => {
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();

    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const contrast = useConfigStore((state) => state.config.ui.contrast);

    const title = detail.title || '';
    const body = detail.contents || '';
    const dateRange = formatListTypeDateRange(detail.eventDateBegin, detail.eventDateEnd, t);
    const dateRangeTTS = formatKoreanDateTTS(detail.eventDateBegin, detail.eventDateEnd, t);
    const url = detail?.thumbnailUrl?.url;
    const urlPreview: string | null | undefined = url ? (isS3UploadUrl(url) ? url : getS3UploadUrl(url)) : '';

    const scrollRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setShowScrollTop(e.currentTarget.scrollTop > 20);
    }, []);

    const handleScrollTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        speak(t('list_type.detail.tts.moved_to_top'));
        // setTimeout(() => {
        //     titleRef.current?.focus();
        // }, 300);
    }, [speak, t]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles['content__header']}>
                    <h2
                        ref={titleRef}
                        className={clsx(styles['content__title'], 'focus-border')}
                        tabIndex={1}
                        data-interactive
                        aria-label={title}
                        data-region-nav
                        data-region-order={1}
                    >
                        {title}
                    </h2>
                    <div className={clsx(styles['content__date'], 'focus-border')} tabIndex={2} data-interactive aria-label={dateRangeTTS}>
                        <CalendarIcon width={18} height={18} color={!contrast ? '#444444' : '#ffffff'} />
                        <span>{dateRange}</span>
                    </div>
                </div>
                <div
                    className={clsx(styles['content__body'], 'scrollbar-custom-gray-99938f scrollbar-w-6', {
                        [styles['content__body--fullsize']]: !detail.poiConnect && !detail.qrConnect,
                    })}
                    ref={scrollRef}
                    onScroll={handleScroll}
                >
                    <div className="focus-border" tabIndex={3} data-interactive aria-label={body} data-region-nav data-region-order={2}>
                        {body}
                    </div>
                    {urlPreview && (
                        <div
                            className={clsx(styles['content__image'], 'focus-border')}
                            tabIndex={4}
                            data-interactive
                            aria-label={t('list_type.detail.tts.detail_image', { title })}
                            data-role={t('common.data_role.image')}
                            data-region-nav
                            data-region-order={3}
                        >
                            <img src={urlPreview} alt={title} loading="lazy" />
                        </div>
                    )}
                </div>
                {(detail.poiConnect || detail.qrConnect) && (
                    <div className={clsx(styles['main__actions'], { [styles['main__actions--halfmode']]: screenMode === 'split' })}>
                        <ListTypeDetailActions
                            onQrClick={onQrClick}
                            onDirectionClick={() => {
                                onDirectionClick?.(detail);
                            }}
                            tabIndexBase={7}
                            showDirection={detail.poiConnect}
                            showQr={detail.qrConnect}
                            qrRegionOrder={5}
                            directionRegionOrder={6}
                        />
                    </div>
                )}

                {showScrollTop && (
                    <button
                        type="button"
                        className={clsx(styles['scroll-top'], 'focus-border', {
                            [styles['scroll-top__halfmode']]: screenMode === 'split',
                        })}
                        onClick={handleScrollTop}
                        aria-label={t('list_type.detail.tts.go_to_top')}
                        tabIndex={5}
                        data-interactive
                        data-role={t('common.data_role.button')}
                        data-region-nav
                        data-region-order={4}
                    >
                        <ChevronUpIcon className={styles['scroll-top__icon']} />
                    </button>
                )}
            </div>
            <div
                className={clsx(styles.footer, {
                    [styles['footer--halfmode']]: screenMode === 'split',
                })}
            >
                <button
                    type="button"
                    className={clsx(styles['footer__back'], 'focus-border', {
                        [styles['footer__back--halfmode']]: screenMode === 'split',
                    })}
                    onClick={onBack}
                    tabIndex={8}
                    data-interactive
                    aria-label={t('list_type.detail.back_to_list')}
                    data-role={t('common.data_role.button')}
                    data-region-nav
                    data-region-order={7}
                >
                    <BackIcon />
                    {t('list_type.detail.back_to_list')}
                </button>
            </div>
        </div>
    );
};

export default ListTypeDetailVertical;
